import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Loader2, Upload, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

// Resize + base64 a picked image so we don't blow Cloudflare's body cap.
const fileToBase64 = (file, max = 1400) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      resolve(dataUrl.split(',')[1]);
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const CHANGE_STYLES = {
  add: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  unchanged: 'bg-slate-50 text-slate-500 border-slate-200',
  unknown: 'bg-red-50 text-red-700 border-red-200',
};

const ImportPlanDialog = ({ open, onClose, api, tripId, planDate, plan, onApplied }) => {
  const [mode, setMode] = useState('text'); // 'text' | 'image'
  const [text, setText] = useState('');
  const [imageBase64, setImageBase64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [diff, setDiff] = useState(null);
  // Per-row checkbox state: { [launchIndex]: { [rowIndex]: true } } and { [launchIndex]: { online_keep: { [diverId]: true } } }
  const [picks, setPicks] = useState({});

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setMode('text'); setText(''); setImageBase64(null); setImagePreview(null);
      setDiff(null); setPicks({});
    }
  }, [open]);

  const buildInitialPicks = (d) => {
    const next = {};
    (d.launches || []).forEach((L) => {
      const rows = {};
      L.matched_rows.forEach((r, i) => { rows[i] = r.change !== 'unknown'; });
      const keep = {};
      L.online_only.forEach((o) => { keep[o.id] = false; }); // default: drop divers not in offline plan
      next[L.launch_index] = { rows, keep };
    });
    return next;
  };

  const runPreview = async () => {
    setPreviewBusy(true);
    setDiff(null);
    try {
      const body = mode === 'text' ? { text } : { image_base64: imageBase64 };
      const d = await api.importPlanPreview(tripId, planDate, body);
      setDiff(d);
      setPicks(buildInitialPicks(d));
      if (d.unknown_count > 0) {
        toast.warning(`${d.unknown_count} name${d.unknown_count === 1 ? '' : 's'} couldn't be matched — add them via the Divers tab first.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not parse plan');
    } finally {
      setPreviewBusy(false);
    }
  };

  const togglePick = (launchIndex, kind, key) => {
    setPicks((prev) => ({
      ...prev,
      [launchIndex]: {
        ...prev[launchIndex],
        [kind]: { ...prev[launchIndex][kind], [key]: !prev[launchIndex][kind][key] },
      },
    }));
  };

  const apply = async () => {
    if (!diff) return;
    // Build the curated launches payload: for each diff launch, collect the chosen diver IDs.
    const launches = (diff.launches || []).map((L) => {
      const chosen = new Set();
      (L.matched_rows || []).forEach((r, i) => {
        if (r.change === 'unknown') return;
        if (picks[L.launch_index]?.rows?.[i] && r.matched_diver_id) chosen.add(r.matched_diver_id);
      });
      (L.online_only || []).forEach((o) => {
        if (picks[L.launch_index]?.keep?.[o.id]) chosen.add(o.id);
      });
      return { launch_index: L.launch_index, diver_ids: Array.from(chosen) };
    });
    setApplyBusy(true);
    try {
      const res = await api.importPlanApply(tripId, planDate, launches);
      await onApplied(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplyBusy(false);
    }
  };

  const onPickImage = async (file) => {
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      setImageBase64(b64);
      setImagePreview(URL.createObjectURL(file));
    } catch (e) {
      toast.error('Could not read that image');
    }
  };

  const launchCount = diff?.launches?.length || 0;
  const totalChanges = (diff?.launches || []).reduce((acc, L) => {
    const add = (L.matched_rows || []).filter((r) => r.change === 'add').length;
    const drop = (L.online_only || []).length;
    return acc + add + drop;
  }, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-plan-dialog">
        <DialogHeader>
          <DialogTitle>Import offline plan · {planDate}</DialogTitle>
        </DialogHeader>

        {!diff && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">Paste a WhatsApp dive plan or upload a screenshot. We&apos;ll match every name against this trip&apos;s diver roster and show you the diff before changing anything.</p>
            <div className="flex gap-2 text-sm">
              <button
                type="button"
                onClick={() => setMode('text')}
                className={`px-3 py-1.5 rounded-full font-medium ${mode === 'text' ? 'bg-[#0A2342] text-white' : 'bg-slate-100 text-[#0A2342]'}`}
                data-testid="import-mode-text"
              >Paste text</button>
              <button
                type="button"
                onClick={() => setMode('image')}
                className={`px-3 py-1.5 rounded-full font-medium ${mode === 'image' ? 'bg-[#0A2342] text-white' : 'bg-slate-100 text-[#0A2342]'}`}
                data-testid="import-mode-image"
              >Upload screenshot</button>
            </div>

            {mode === 'text' ? (
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={12}
                placeholder={`07h00 dives\nKatinka\nCorne\nEste\nAndre\n\n09h30 dives\nBryan\nKiara\n...`}
                className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-[#FF6B35] font-mono"
                data-testid="import-text-input"
              />
            ) : (
              <div className="space-y-2">
                <label className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg cursor-pointer hover:bg-slate-200 text-sm">
                  <Upload className="w-4 h-4" />
                  {imageBase64 ? 'Choose a different screenshot' : 'Choose a WhatsApp screenshot'}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onPickImage(e.target.files?.[0])} data-testid="import-image-input" />
                </label>
                {imagePreview && (
                  <img src={imagePreview} alt="preview" className="max-h-80 rounded-lg border" />
                )}
                <p className="text-[11px] text-gray-500">PNG/JPEG/WEBP only. Image is resized to ~1400px and sent to Claude Vision for OCR — takes 5-15s.</p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={runPreview}
                disabled={previewBusy || (mode === 'text' ? !text.trim() : !imageBase64)}
                className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
                data-testid="import-preview-btn"
              >
                {previewBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Preview diff
              </Button>
            </DialogFooter>
          </div>
        )}

        {diff && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm bg-slate-50 rounded-lg p-3 border">
              <div>
                <div className="font-semibold text-[#0A2342]">{launchCount} launch{launchCount === 1 ? '' : 'es'} parsed</div>
                <div className="text-xs text-gray-500">{totalChanges} change{totalChanges === 1 ? '' : 's'} pending · {diff.unknown_count} unknown name{diff.unknown_count === 1 ? '' : 's'}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setDiff(null)} className="text-xs">Re-parse</Button>
            </div>

            {diff.unknown_count > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-xs p-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>Unknown names won&apos;t be added. Open the Divers tab, add them as new divers, then re-run this import.</div>
              </div>
            )}

            {diff.launches.map((L) => (
              <div key={L.launch_index} className="border rounded-xl bg-white" data-testid={`diff-launch-${L.launch_index}`}>
                <div className="px-3 py-2 border-b bg-slate-50 rounded-t-xl flex items-center justify-between text-xs">
                  <div className="font-semibold text-[#0A2342]">
                    Offline: <span className="text-[#FF6B35]">{L.offline_label}</span>
                    {L.online_label && <> &nbsp;→&nbsp; Online: <span className="text-[#0A2342]">{L.online_label}</span></>}
                  </div>
                </div>
                <ul className="divide-y text-sm">
                  {L.matched_rows.map((r, i) => {
                    const checked = picks[L.launch_index]?.rows?.[i] || false;
                    const styleKey = r.change === 'unknown' ? 'unknown' : r.change === 'add' ? 'add' : 'unchanged';
                    return (
                      <li key={`r-${i}`} className="flex items-center gap-3 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={r.change === 'unknown'}
                          onChange={() => togglePick(L.launch_index, 'rows', i)}
                          data-testid={`diff-row-pick-${L.launch_index}-${i}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#0A2342] truncate">{r.matched_name || r.raw_name}</div>
                          {r.matched_name && r.raw_name && r.matched_name.toLowerCase() !== r.raw_name.toLowerCase() && (
                            <div className="text-[11px] text-gray-400">matched from &ldquo;{r.raw_name}&rdquo;</div>
                          )}
                        </div>
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full font-semibold tracking-wide border ${CHANGE_STYLES[styleKey]}`}>
                          {r.change === 'add' ? 'Add' : r.change === 'unknown' ? 'Unknown' : 'Keep'}
                        </span>
                      </li>
                    );
                  })}
                  {L.online_only.length > 0 && (
                    <li className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 bg-amber-50 border-t">In online but not in offline</li>
                  )}
                  {L.online_only.map((o) => {
                    const checked = picks[L.launch_index]?.keep?.[o.id] || false;
                    return (
                      <li key={`o-${o.id}`} className="flex items-center gap-3 px-3 py-2 bg-amber-50/40">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePick(L.launch_index, 'keep', o.id)}
                          data-testid={`diff-keep-${L.launch_index}-${o.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#0A2342] truncate">{o.name}</div>
                        </div>
                        <span className="text-[10px] uppercase px-2 py-0.5 rounded-full font-semibold tracking-wide border bg-amber-100 text-amber-800 border-amber-300">
                          {checked ? 'Keep' : 'Drop'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={applyBusy}>Cancel</Button>
              <Button onClick={apply} disabled={applyBusy} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="import-apply-btn">
                {applyBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Apply ticked changes
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportPlanDialog;
